import { Component, OnInit, OnChanges, OnDestroy, Input, Output, SimpleChanges, ChangeDetectorRef, EventEmitter } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { MenuItem, MeteringPoint } from '@model';
import { SxNavigationMenuService, SxPermissionService } from '@services';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-navigation-menu',
  standalone: false,
  templateUrl: './navigation-menu.component.html'
})
export class NavigationMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  menuItems: MenuItem[] = [];
  @Input()
  meteringPoints: { meteringPoints: MeteringPoint[]; loaded: boolean };
  @Input()
  isExternalCustomersOnly: boolean;
  @Input()
  isHandset: boolean;
  @Input()
  isExpanded: boolean;
  @Input()
  allowedWhiteLabelMenuItemIds: string[] = [];
  @Output()
  navigationClick = new EventEmitter<MenuItem>();
  @Output()
  expandChange = new EventEmitter<boolean>();

  navigationItems: MenuItem[] = [];
  selectedItem: MenuItem;
  
  private subs = new SubSink();
  
  // Constants
  private readonly TOP_NODE_ID = '-1';

  constructor(
        public router: Router,
        public navigationMenuService: SxNavigationMenuService,
        private permissionService: SxPermissionService,
        private translate: TranslateService,
        private changeDetectorRef: ChangeDetectorRef
  ) {}

  get isMeteringPointsLoaded(): boolean {
    if (!(this.meteringPoints) || !this.meteringPoints.loaded) {
      return false;
    }
    return this.meteringPoints.meteringPoints.length > 0;
  }
 
  get topLevelMenuItems(): MenuItem[] {
    if (this.navigationItems.length === 0) {
      return [];
    }

    return this.navigationItems.filter(item => item.parentId === this.TOP_NODE_ID);
  }

  get isMenuExpanded(): boolean {
    if (this.isHandset) 
      return true; 

    return this.isExpanded;
  }

  get expansionMenuButtonTooltip(): string{
    return this.isMenuExpanded ? this.translate.instant('core.sidebar.collapse_menu') : this.translate.instant('core.sidebar.expand_menu');
  }

  ngOnInit(): void {
    this.initMenuItems();

    
    this.subs.sink = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(event => {

        // Check if the current route matches any menu item
        const currentUrl = event.urlAfterRedirects || event.url;
        let menuItem = this.navigationMenuService.getMatchedObjectByUrl(this.navigationItems, currentUrl);
        //console.log(`navigation-menu\tNavigationEnd\tmatched menu item:`, menuItem);

        if (menuItem && !this.navigationItems.some(item => item.active)) {
          this.setActiveItem(menuItem);
        }
        this.changeDetectorRef.detectChanges();
      });
      
      // Handle browser navigation (back/forward)
      this.subs.sink = fromEvent(window, 'popstate').subscribe((event: PopStateEvent) => {
        const currentUrl = window.location.pathname;
        //console.log(`navigation-menu\tpopstate\tcurrentUrl:`, currentUrl);
        let menuItem = this.navigationMenuService.getMatchedObjectByUrl(this.navigationItems, currentUrl);
        //console.log(`navigation-menu\tpopstate\tmatched menu item:`, menuItem);
        if (menuItem) {
          this.setActiveItem(menuItem);
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    for (const propName in changes) {
      if (!changes.hasOwnProperty(propName)) 
        continue;
      
      switch(propName){
        case 'isExternalCustomersOnly':
          //console.log(`navigation-menu\tngOnChanges\tisExternalCustomersOnly:`, this.isExternalCustomersOnly);
          this.initMenuItems();
          break;
        case 'menuItems':
          if (this.menuItems && this.menuItems.length > 0) {
            //console.log(`navigation-menu\tngOnChanges\tmenuItems:`);
            this.initMenuItems();
          }
          break;
        case 'meteringPoints':
          if (this.isMeteringPointsLoaded){
            //console.log(`navigation-menu\tngOnChanges\tmeteringPoints:`, this.meteringPoints.meteringPoints);
            this.initMenuItems();
          }
          break;
        case 'allowedWhiteLabelMenuItemIds':
          if (this.allowedWhiteLabelMenuItemIds && this.allowedWhiteLabelMenuItemIds.length > 0)
          {
            //console.log(`navigation-menu\tngOnChanges\tallowedWhiteLabelMenuItemIds:`, this.allowedWhiteLabelMenuItemIds);
            this.initMenuItems();
          }
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onMenuItemClick(item: MenuItem): void {
    if (item.externalRedirect || item.disabled) {
      return;
    }
    
    this.setActiveItem(item);
    this.router.navigate([item.linkUrl]);

    if (this.isHandset){
      this.navigationClick.emit(item);
    }
  }

  onExpandClick(): void {
    this.expandChange.emit(!this.isExpanded);
  }

  private initMenuItems(): void {
    this.navigationItems = [];

    if (!(!!this.menuItems) || this.menuItems.length === 0) 
      return;
         
    let clonedMenuItems =  [...this.menuItems
      .filter(item => !item.hidden)
      .map(item => this.cloneMenuItem(item))
    ];

    this.navigationMenuService.addContentIdToLink(clonedMenuItems);
    this.navigationMenuService.tryTranslate(clonedMenuItems, this.translate);

    if (clonedMenuItems.length === 0) 
      return;

    this.navigationItems = clonedMenuItems.flatMap(item => !item.children || item.children.length === 0 ? [ item ] : [ item, ...item.children.flat() ] );

    if (!this.selectedItem){
      let matchedMenuItem = this.navigationMenuService.getMatchedObjectByUrl(this.navigationItems, this.router.url);
      this.setActiveItem(matchedMenuItem);
    }
  }

  private setActiveItem(item: MenuItem): void {
    if (!item) {
      return;
    }

   this.navigationItems = this.navigationItems.map(menuItem => ({...menuItem, active: false }));

    // set the active item
    this.navigationItems.find(menuItem => menuItem.id === item.id).active = true;
    this.selectedItem = item;
    //console.log(`navigation-menu\tsetActiveItem\tselectedItem:`, this.selectedItem);
  }

  private cloneMenuItem(node: MenuItem): MenuItem {

    //console.log(`navigation-menu\tcloneMenuItem\tid: ${node.id} isExternalCustomersOnly: ${this.isExternalCustomersOnly} isDisabled: ${this.isExternalCustomersOnly && !this.allowedWhiteLabelMenuItemIds.includes(node.id)}`);
    return <MenuItem>{
      ...node,
      expanded: false,
      active: node.active || (!!this.selectedItem && node.id === this.selectedItem.id),
      checkData: node.checkData ? node.checkData : false,
      disabled: (node.checkData && !this.isMeteringPointsLoaded) || (this.isExternalCustomersOnly && !this.allowedWhiteLabelMenuItemIds.includes(node.id)),
      hidden: node.hidden ? node.hidden : false,
      externalRedirect: node.externalRedirect ? node.externalRedirect : false,
      parentId: node.parentId ? node.parentId : this.TOP_NODE_ID,
      iconName: node.iconName ? node.iconName : "",
      children: node.children && Array.isArray(node.children) ? [...node.children.filter(x => !x.hidden).map(child => this.cloneMenuItem(child))] : [],
    };
  }

  getChildren(item: MenuItem): MenuItem[] {
    if (!item.children || item.children.length === 0) {
      return [];
    }

    return this.navigationItems.filter(menuItem => menuItem.parentId === item.id);
  }
  
  hasPermission(item: MenuItem): boolean {
    return this.permissionService.securityTrim(item.userTypes, item.permissions, null, null);
  }
}
